/*
Phase status screens for the inspection-scheduling page (STREAM-38 v3).

The tokenized link is a lifecycle page: once availability/answers are in, the
same URL renders live order state instead of the capture wizard. One screen
per phase (matching / confirmed / day-of / complete), all presentational —
the page routes on the GET payload's status block.

Copy note (locked decision): V1 never promises text messages. Every screen
says "check back here" / "your lender will follow up" instead.
*/

import React from 'react'
import moment from 'moment-timezone'

import { InfoCard, InfoRow, Screen, StatusPill, SuccessRing } from './chrome'
import { buildIcs, downloadIcs } from './calendar'

/* Derivation helpers ------------------------------------------------------- */

export const tzMoment = (value, timezone) =>
  timezone ? moment(value).tz(timezone) : moment(value)

// "10 AM" / "10:30 AM" — drop :00 like the mockup's time labels.
export const heroTime = m => (m.minute() === 0 ? m.format('h A') : m.format('h:mm A'))

export const tzAbbr = timezone => (timezone ? moment.tz(timezone).format('z') : '')

export const windowRange = (event, timezone) => {
  const start = tzMoment(event.start, timezone)
  if (!event.end) {
    return heroTime(start)
  }
  return heroTime(start) + ' – ' + heroTime(tzMoment(event.end, timezone))
}

// Map an answer value back to its option label; free-text values (from an
// "other" selection or text questions) render verbatim.
export const answerLabel = (page, key) => {
  const answers = page.answers || {}
  const value = answers[key]
  if (!value) {
    return null
  }
  const questions = (page.questionnaire && page.questionnaire.questions) || []
  const question = questions.find(q => q.key === key)
  const option = question && (question.options || []).find(o => o.value === value)
  return option ? option.label : value
}

// Day-of contact: the answered best_contact if we can resolve it, otherwise
// the link's recipient.
export const dayOfContact = page => {
  const contacts = page.contacts || []
  const answered = (page.answers || {}).best_contact
  const contact = contacts.find(c => c.id === answered) || page.contact
  if (!contact) {
    return null
  }
  const phone = contact.cell_phone || contact.home_phone || contact.work_phone
  return { name: contact.full_name, phone: phone || null }
}

/* Matching ----------------------------------------------------------------- */

export const MatchingScreen = ({ page, lenderName }) => {
  const timezone = page.order && page.order.property_timezone
  const abbr = tzAbbr(timezone)
  const events = (page.availability || []).filter(event => event.start)
  const first = events[0]
  const access = answerLabel(page, 'access_method')
  const contact = dayOfContact(page)

  return (
    <Screen lenderName={lenderName} testId='scheduling-matching'>
      <StatusPill tone='progress' label='Finding your inspector' />
      <h1 className='isp-display isp-serif'>Got it &mdash; we&rsquo;re matching you</h1>
      <p className='isp-lede'>
        We&rsquo;re checking your times against inspector availability.
        Check back here for updates &mdash; nothing else is needed from you right now.
      </p>
      <InfoCard>
        {access && <InfoRow label='Access'>{access}</InfoRow>}
        {first && (
          <InfoRow
            label='Your times'
            sub={
              windowRange(first, timezone) + (abbr ? ' ' + abbr : '') +
              (events.length > 1 ? ' · +' + (events.length - 1) + ' more' : '')
            }
          >
            {tzMoment(first.start, timezone).format('ddd, MMM D')}
          </InfoRow>
        )}
        {contact && <InfoRow label='Day-of contact'>{contact.name}</InfoRow>}
        <InfoRow label='Property'>{page.order && page.order.address}</InfoRow>
      </InfoCard>
    </Screen>
  )
}

/* Confirmed ---------------------------------------------------------------- */

export const ConfirmedScreen = ({ page, status, lenderName }) => {
  const timezone = page.order && page.order.property_timezone
  const abbr = tzAbbr(timezone)
  const slot = status.confirmed_window || {}
  const start = slot.start ? tzMoment(slot.start, timezone) : null
  const provider = status.provider
  const contact = dayOfContact(page)
  const address = page.order && page.order.address

  const addToCalendar = () => {
    const ics = buildIcs({
      start: slot.start,
      end: slot.end,
      summary: 'Property inspection',
      location: address,
      description: 'Scheduled by Reggora' + (lenderName ? ' on behalf of ' + lenderName : ''),
      uid: page.order && page.order.id,
    })
    downloadIcs(ics, 'inspection.ics')
  }

  return (
    <Screen lenderName={lenderName} testId='scheduling-confirmed'>
      <StatusPill tone='confirm' label='Confirmed' />
      <h1 className='isp-display isp-serif'>{start ? start.format('dddd, MMMM D') : 'You’re confirmed'}</h1>
      {start && (
        <div className='isp-time-hero' data-testid='time-hero'>
          {windowRange(slot, timezone)} {abbr && <span className='tz'>{abbr}</span>}
        </div>
      )}
      <p className='isp-lede'>
        You&rsquo;re all set &mdash; nothing to do until then. Check back here for any updates.
      </p>
      <InfoCard>
        {provider && provider.name && (
          <InfoRow label='Inspector' sub={provider.company}>{provider.name}</InfoRow>
        )}
        {address && <InfoRow label='Property'>{address}</InfoRow>}
        {contact && (
          <InfoRow label='Day-of contact'>
            {contact.name}{contact.phone ? ' · ' + contact.phone : ''}
          </InfoRow>
        )}
      </InfoCard>
      {start && (
        <div className='isp-cta-stack'>
          <button className='isp-cta' data-testid='add-to-calendar' onClick={addToCalendar}>
            Add to calendar
          </button>
        </div>
      )}
    </Screen>
  )
}

/* Day of ------------------------------------------------------------------- */

export const DayOfScreen = ({ page, status, lenderName }) => {
  const timezone = page.order && page.order.property_timezone
  const abbr = tzAbbr(timezone)
  const slot = status.confirmed_window || {}
  const provider = status.provider
  const access = answerLabel(page, 'access_method')
  const notes = (page.answers || {}).additional_notes

  const who = provider && provider.name ? provider.name : 'Your inspector'
  const when = slot.start
    ? ' is scheduled for ' + windowRange(slot, timezone) + (abbr ? ' ' + abbr : '') + ' today.'
    : ' is scheduled for today.'

  return (
    <Screen lenderName={lenderName} testId='scheduling-dayof'>
      <StatusPill tone='dayof' label='Today' />
      <h1 className='isp-display isp-serif'>Today&rsquo;s the inspection</h1>
      <p className='isp-lede'>
        <b>{who}</b>{when} Here&rsquo;s a recap of what you told us.
      </p>
      <InfoCard>
        {provider && provider.name && (
          <InfoRow label='Inspector' sub={provider.company}>{provider.name}</InfoRow>
        )}
        {access && <InfoRow label='Access'>{access}</InfoRow>}
        <InfoRow label='Your notes'>{notes || '—'}</InfoRow>
      </InfoCard>
    </Screen>
  )
}

/* Complete ------------------------------------------------------------------ */

export const CompleteScreen = ({ page, status, lenderName }) => {
  const timezone = page.order && page.order.property_timezone
  const abbr = tzAbbr(timezone)
  const provider = status.provider
  const firstName = page.contact && page.contact.firstname
  const address = page.order && page.order.address
  const completed = status.completed_at
    ? tzMoment(status.completed_at, timezone).format('MMM D, YYYY · h:mm A') + (abbr ? ' ' + abbr : '')
    : null

  return (
    <Screen lenderName={lenderName} centered testId='scheduling-complete'>
      <SuccessRing />
      <StatusPill tone='complete' label='Complete' />
      <h1 className='isp-display isp-serif'>Inspection complete</h1>
      <p className='isp-lede'>
        Thanks{firstName ? ', ' + firstName : ''}. The inspection{address ? ' at ' + address : ''} is
        done &mdash; nothing else is needed from you.
      </p>
      {(completed || (provider && provider.name)) && (
        <InfoCard>
          {completed && <InfoRow label='Completed'>{completed}</InfoRow>}
          {provider && provider.name && <InfoRow label='Inspector'>{provider.name}</InfoRow>}
        </InfoCard>
      )}
    </Screen>
  )
}
